import { DIALOG_EVENT, SNACKBAR_SEVERITY, UI_ELEMENTS } from "Common/Constants";
import type { IUIElementState } from "Common/Interfaces";
import { useState } from "react";
import { useGetAllowedFileTypesQuery } from "Services/Document";

interface IUseReportBug {
  handleClose: () => void;
  open?: boolean;
}

export const useReportBug = (props: IUseReportBug) => {
  const { handleClose, open } = props;

  // UI Element State
  const [uiElementState, setUIElementState] = useState<IUIElementState<unknown>>({
    anchorEl: null,
    activeElement: null,
    dataRow: null,
  });

  const handleCloseUIElement = () => {
    setUIElementState((prevState) => ({
      ...prevState,
      anchorEl: null,
      activeElement: null,
    }));
  };

  // Handle Cancel Confirmation Dialog Open
  const handleCancelConfirmationDialogOpen = () => {
    setUIElementState((prevState) => ({
      ...prevState,
      anchorEl: null,
      activeElement: UI_ELEMENTS.CANCEL_CONFIRMATION,
    }));
  };

  // Handle Open Snackbar
  const handleOpenSnackbar = (message: string, isSuccess: boolean) => {
    setUIElementState((prevState) => ({
      ...prevState,
      anchorEl: null,
      activeElement: UI_ELEMENTS.SNACKBAR,
      snackbarMessage: message,
      snackbarSeverity: isSuccess ? SNACKBAR_SEVERITY.SUCCESS : SNACKBAR_SEVERITY.ERROR,
    }));
  };

  const handleConfirmationDialogEvent = (event: DIALOG_EVENT) => {
    if (event === DIALOG_EVENT.CANCEL) {
      handleCloseUIElement();
    } else if (event === DIALOG_EVENT.CONFIRM) {
      if (
        uiElementState.activeElement === UI_ELEMENTS.LIMIT_EXCEEDED_CONFIRMATION ||
        uiElementState.activeElement === UI_ELEMENTS.INVALID_CONFIRMATION
      ) {
        setRejectedFiles([]);
        handleCloseUIElement();
      }
      if (uiElementState.activeElement === UI_ELEMENTS.CANCEL_CONFIRMATION) {
        handleClose();
        handleCloseUIElement();
      }
    }
  };

  // Fetch Allowed File Types
  const { data: fileTypeQuery, isLoading: isLoadingFileTypes } = useGetAllowedFileTypesQuery(undefined, { skip: !open });
  const allowedFileTypes: string[] = fileTypeQuery?.value.split(",").filter((val: string) => val !== "mp4") ?? [];

  // Check the files dropped
  const [rejectedFiles, setRejectedFiles] = useState<File[]>([]);

  const handleOpenConfirmationDialog = (activeElement: UI_ELEMENTS) => {
    setUIElementState((prevState) => ({
      ...prevState,
      open: true,
      activeElement,
    }));
  };

  const handleFilesDropped = (newFiles: File[], existingFiles: File[]): File[] => {
    const maxFiles = 5;
    const allowedExtensions = new Set(allowedFileTypes.map((type) => type.toLowerCase())); // Convert allowed types to lowercase
    const invalidFiles = newFiles.filter((file) => {
      const fileExtension = file.name.split(".").pop()?.toLowerCase(); // Extract the file extension
      return !fileExtension || !allowedExtensions.has(fileExtension);
    });

    const totalFiles = existingFiles.length + newFiles.length;

    if (totalFiles > maxFiles) {
      handleOpenConfirmationDialog(UI_ELEMENTS.LIMIT_EXCEEDED_CONFIRMATION);
      return existingFiles;
    }

    if (invalidFiles.length > 0) {
      setRejectedFiles(invalidFiles);
      handleOpenConfirmationDialog(UI_ELEMENTS.INVALID_CONFIRMATION);
      return existingFiles;
    }

    // If all conditions are met, add the new files to the existing list
    return [...existingFiles, ...newFiles];
  };

  return {
    uiElementState,
    isLoadingFileTypes,
    rejectedFiles,
    handleCloseUIElement,
    handleCancelConfirmationDialogOpen,
    handleConfirmationDialogEvent,
    handleFilesDropped,
    handleOpenSnackbar,
  };
};
